import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  TextField,
  ListSubheader,
  type SelectChangeEvent,
} from "@mui/material";

export const INFINITE_SCROLL_PAGE_SIZE = 20;
const LOAD_AHEAD_ITEMS = 3;
const ITEM_HEIGHT_PX = 48;

interface PaginatedSelectProps<T> {
  field: {
    value: any;
    onChange: (value: any) => void;
    onBlur?: () => void;
  };
  label?: string;
  options?: T[];
  idKey?: keyof T;
  nameKey?: keyof T;
  isMulti?: boolean;
  disabled?: boolean;
  enableSearch?: boolean;
  returnObject?: boolean;
  onFetchPage: (page: number, search: string) => Promise<{ items: T[]; total: number }>;
  /** Change when parent closes (e.g. filter popover) to reset list/search cache */
  resetKey?: number;
  variant?: "standard" | "outlined";
  size?: "small" | "medium";
  placeholder?: string;
  hideLabel?: boolean;
  sx?: any;
}

const PaginatedSelect = <T extends Record<string, any>>({
  field,
  label,
  options = [],
  idKey = "Id",
  nameKey = "Name",
  isMulti = true,
  disabled = false,
  enableSearch = true,
  returnObject = true,
  onFetchPage,
  resetKey = 0,
  variant = "standard",
  size = "medium",
  placeholder,
  hideLabel = false,
  sx,
}: PaginatedSelectProps<T>) => {
  const [searchValue, setSearchValue] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [listOptions, setListOptions] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const userLoadedMoreRef = useRef(false);
  const prefetchedKeyRef = useRef("");
  const manualChangeRef = useRef(false);

  const selectedValue = field?.value ?? (isMulti ? [] : "");

  const isSameId = (a: any, b: any) => String(a) === String(b);

  const optionsKey = useMemo(
    () => options.map((item) => String(item?.[idKey] ?? "")).join("|"),
    [options, idKey]
  );

  const setHasMoreFromPage = (items: T[], loadedCount: number, total: number) => {
    if (total > loadedCount) {
      setHasMore(true);
      return;
    }
    setHasMore(items.length >= INFINITE_SCROLL_PAGE_SIZE);
  };

  const resetToPrefetchedOptions = useCallback(() => {
    setSearchValue("");
    setMenuOpen(false);
    setListOptions(options);
    pageRef.current = 1;
    setHasMore(options.length >= INFINITE_SCROLL_PAGE_SIZE);
    userLoadedMoreRef.current = false;
    prefetchedKeyRef.current = optionsKey;
    fetchingRef.current = false;
    setLoadingMore(false);
  }, [options, optionsKey]);

  useEffect(() => {
    resetToPrefetchedOptions();
  }, [resetKey, resetToPrefetchedOptions]);

  const fetchPage = useCallback(
    async (pageNum: number, search: string, replace: boolean) => {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      setLoadingMore(true);
      try {
        const { items, total } = await onFetchPage(pageNum, search);
        userLoadedMoreRef.current = !replace;
        setListOptions((prev) => {
          const next = replace ? items : [...prev, ...items];
          setHasMoreFromPage(items, next.length, total);
          return next;
        });
        pageRef.current = pageNum;
      } catch {
        if (replace) setListOptions([]);
        setHasMore(false);
      } finally {
        setLoadingMore(false);
        fetchingRef.current = false;
      }
    },
    [onFetchPage]
  );

  useEffect(() => {
    if (options.length === 0 || userLoadedMoreRef.current) return;
    if (prefetchedKeyRef.current === optionsKey) return;
    prefetchedKeyRef.current = optionsKey;
    setListOptions(options);
    pageRef.current = 1;
    setHasMore(options.length >= INFINITE_SCROLL_PAGE_SIZE);
  }, [options, optionsKey]);

  useEffect(() => {
    if (!menuOpen) return;
    const search = searchValue.trim();
    const timer = setTimeout(() => {
      if (!search && listOptions.length > 0) return;
      fetchPage(1, search, true);
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [searchValue, menuOpen, fetchPage, listOptions.length]);

  const currentIds = useMemo(() => {
    if (!isMulti) return returnObject ? selectedValue?.[idKey] : selectedValue;
    const ids = returnObject
      ? (selectedValue as any[])?.map((x) => x?.[idKey])
      : selectedValue;
    return (ids || []).filter((id: any) => id !== undefined && id !== null && id !== "");
  }, [selectedValue, isMulti, returnObject, idKey]);

  const findById = useCallback(
    (id: any) => {
      const fromSelected = Array.isArray(selectedValue)
        ? selectedValue.find((x: any) => isSameId(x?.[idKey], id))
        : isSameId(selectedValue?.[idKey], id)
          ? selectedValue
          : undefined;
      return (fromSelected as T) ?? listOptions.find((x) => isSameId(x[idKey], id));
    },
    [selectedValue, listOptions, idKey]
  );

  const safeValue = useMemo(() => {
    if (isMulti) return (currentIds || []).map((id: any) => String(id));
    if (currentIds === undefined || currentIds === null || currentIds === "") return "";
    return String(currentIds);
  }, [currentIds, isMulti]);

  const applySelection = useCallback(
    (ids: any[]) => {
      if (!returnObject) {
        field.onChange(ids);
        return;
      }
      const prevSelected = Array.isArray(selectedValue) ? selectedValue : [];
      const objs = ids
        .map((id) => findById(id) ?? prevSelected.find((x: any) => isSameId(x?.[idKey], id)))
        .filter(Boolean);
      field.onChange(objs);
    },
    [field, findById, returnObject, selectedValue, idKey]
  );

  const toggleOption = useCallback(
    (optionId: any) => {
      const ids = (currentIds || []).map((id: any) => String(id));
      const idStr = String(optionId);
      const nextIds = ids.some((id: any) => isSameId(id, idStr))
        ? ids.filter((id: any) => !isSameId(id, idStr))
        : [...ids, idStr];

      manualChangeRef.current = true;
      applySelection(nextIds);
    },
    [currentIds, applySelection]
  );

  const handleMenuScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (loadingMore || !hasMore || fetchingRef.current) return;
      const el = event.currentTarget;
      const loadAheadPx = LOAD_AHEAD_ITEMS * ITEM_HEIGHT_PX;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - loadAheadPx) {
        fetchPage(pageRef.current + 1, searchValue.trim(), false);
      }
    },
    [loadingMore, hasMore, fetchPage, searchValue]
  );

  const handleChange = (event: SelectChangeEvent<any>) => {
    if (manualChangeRef.current) {
      manualChangeRef.current = false;
      return;
    }

    const raw = event.target.value;
    const ids = (Array.isArray(raw) ? raw : raw === "" ? [] : [raw]).filter(
      (id) => id !== "" && listOptions.some((o) => isSameId(o[idKey], id))
    );
    applySelection(ids);
  };

  const renderValue = useCallback(
    (selected: any) => {
      if (!Array.isArray(selected) || selected.length === 0)
        return placeholder || "";
      const first = findById(selected[0]);
      const count = selected.length - 1;
      return count > 0
        ? `${first?.[nameKey]} (+${count} other${count > 1 ? "s" : ""})`
        : first?.[nameKey];
    },
    [findById, nameKey, placeholder]
  );

  const showFloatingLabel = !!label && !hideLabel && variant !== "outlined";

  const isEmptyValue = Array.isArray(safeValue)
    ? safeValue.length === 0
    : safeValue === "" || safeValue === undefined || safeValue === null;

  const mergedSelectSx = {
    ...sx,
    ...(placeholder && isEmptyValue
      ? { "& .MuiSelect-select": { color: "#9ca3af" } }
      : {}),
  };

  const showInitialLoading = loadingMore && listOptions.length === 0;

  return (
    <FormControl variant={variant} size={size} fullWidth disabled={disabled}>
      {showFloatingLabel && <InputLabel>{label}</InputLabel>}

      <MUISelect
        multiple={isMulti}
        value={safeValue}
        onChange={handleChange}
        onBlur={() => field.onBlur?.()}
        displayEmpty
        sx={mergedSelectSx}
        {...(showFloatingLabel ? { label } : {})}
        renderValue={renderValue}
        onOpen={() => {
          setMenuOpen(true);
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }}
        onClose={() => {
          resetToPrefetchedOptions();
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            onScroll: handleMenuScroll,
            sx: {
              maxHeight: 220,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            },
          },
          MenuListProps: {
            autoFocusItem: false,
            sx: { pt: 0 },
          },
        }}
      >
        {enableSearch && (
          <ListSubheader
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: "#fff",
              lineHeight: "normal",
              py: 1,
            }}
          >
            <TextField
              inputRef={searchInputRef}
              variant="standard"
              fullWidth
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </ListSubheader>
        )}

        {showInitialLoading ? (
          <MenuItem disabled>Loading...</MenuItem>
        ) : listOptions.length > 0 ? (
          [
            ...listOptions.map((opt) => {
              const optionId = opt[idKey];
              const selectValue = String(optionId);
              const checked = currentIds?.some((id: any) => isSameId(id, optionId));

              return (
                <MenuItem
                  key={selectValue}
                  value={selectValue}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleOption(optionId);
                  }}
                  sx={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {isMulti && (
                    <Checkbox
                      checked={!!checked}
                      tabIndex={-1}
                      disableRipple
                      sx={{ pointerEvents: "none" }}
                    />
                  )}
                  <ListItemText primary={opt[nameKey]} />
                </MenuItem>
              );
            }),
            ...(loadingMore
              ? [
                  <MenuItem key="loading-more" disabled sx={{ justifyContent: "center" }}>
                    <CircularProgress size={18} />
                  </MenuItem>,
                ]
              : []),
          ]
        ) : (
          <MenuItem disabled>No results found</MenuItem>
        )}
      </MUISelect>
    </FormControl>
  );
};

export default PaginatedSelect;
